<script>
import ProfileLayout from "@/Layouts/ProfileLayout.vue";

export default {
    layout: ProfileLayout,
};
</script>

<script setup>
import { computed, onMounted } from "vue";
import { Head, Link, router, usePage } from "@inertiajs/vue3";
import { useModalogStore } from "@/stores/modalog.js";
import { useWishlistStore } from "@/stores/wishlist.js";
import ConfirmedGiftsToggle from "@/Components/Profile/ConfirmedGiftsToggle.vue";
import FollowButton from "@/Components/Profile/FollowButton.vue";
import GiftCard from "@/Components/Profile/GiftCard.vue";
import ListsPagination from "@/Components/Profile/ListsPagination.vue";
import PromoBanner from "@/Components/Profile/PromoBanner.vue";
import SortDropdown from "@/Components/Profile/SortDropdown.vue";
import WishListCard from "@/Components/Profile/WishListCard.vue";
import WishListDetailHero from "@/Components/Profile/WishListDetailHero.vue";
import WishListMasthead from "@/Components/Profile/WishListMasthead.vue";

const modalogStore = useModalogStore();
const wishlistStore = useWishlistStore();

const page = usePage();

const owner = computed(() => page.props.owner?.data);
const giftlist = computed(() => page.props.giftlist?.data);
const items = computed(() => page.props.items?.data ?? []);
const meta = computed(() => page.props.items?.meta);
const moreLists = computed(() => page.props.moreLists?.data ?? []);

const isConfirmedView = computed(() => route().current() === "list.confirmed");
const isExpertList = computed(() => giftlist.value?.type === "expert");
// Gift Heroes' expert lists get the editorial header (GH-1299).
const isGiftHeroExpertList = computed(
    () => isExpertList.value && !!owner.value?.is_gift_hero,
);

const totalGifts = computed(() => meta.value?.total ?? items.value.length);

const giftsHeading = computed(
    () =>
        `${totalGifts.value} ${totalGifts.value === 1 ? "Gift" : "Gifts"} In This List`,
);

// The reveal only lists gifts with a confirmed purchase (GH-1505).
const confirmedHeading = computed(
    () =>
        `${totalGifts.value} Confirmed ${totalGifts.value === 1 ? "Gift" : "Gifts"}`,
);

const ownerShortName = computed(() =>
    owner.value?.is_organization
        ? owner.value.presentation_name
        : owner.value?.full_name?.split(" ")[0] ||
          owner.value?.presentation_name,
);

// Everyone but the owner gets the reveal (GH-853); expert lists have no
// confirmations, and an empty list has nothing to reveal yet (BH-37).
const showConfirmedButton = computed(
    () => !isExpertList.value && !isConfirmedView.value && totalGifts.value > 0,
);

// --- Meta tags (parity with the owner detail page) ---
const ownerName = computed(() => owner.value?.presentation_name);

// Expert lists are credited to the expert's full display name (a brand or
// persona like "Animal Lover"), not a first name.
const moreListsOwnerName = computed(() =>
    isExpertList.value ? ownerName.value : ownerShortName.value,
);
const openGraph = computed(() => giftlist.value?.open_graph);
const pageDescription = computed(
    () =>
        `${ownerName.value}'s "${giftlist.value?.name}" Wish List on Gift Hero`,
);
const noIndex = computed(
    () =>
        giftlist.value?.privacy === "private" ||
        (!owner.value?.is_gift_hero && !owner.value?.is_organization),
);

// --- Server-side sorting (Inertia partial reloads) ---
const SORT_OPTIONS = [
    {
        value: "recent",
        label: "Most Recent",
        orderBy: "sequence",
        sortDirection: "desc",
    },
    {
        value: "oldest",
        label: "Oldest First",
        orderBy: "created_at",
        sortDirection: "asc",
    },
    {
        value: "price-low",
        label: "Price: Low to High",
        orderBy: "price",
        sortDirection: "asc",
    },
    {
        value: "price-high",
        label: "Price: High to Low",
        orderBy: "price",
        sortDirection: "desc",
    },
];

const sort = computed({
    get() {
        const current = page.props.sortOption;

        return (
            SORT_OPTIONS.find(
                (option) =>
                    option.orderBy === current?.orderBy &&
                    option.sortDirection === current?.sortDirection,
            )?.value ?? SORT_OPTIONS[0].value
        );
    },
    set(value) {
        const option = SORT_OPTIONS.find((o) => o.value === value);

        router.reload({
            data: {
                orderBy: option.orderBy,
                sortDirection: option.sortDirection,
                page: 1,
            },
            only: ["items", "sortOption"],
            preserveState: true,
        });
    },
});

const showSort = computed(
    () => !isConfirmedView.value && totalGifts.value >= 2,
);

function goToPage(n) {
    router.reload({
        data: { page: n },
        only: ["items", "sortOption"],
        preserveState: true,
    });
}

function openSpoilerModalog() {
    modalogStore.showModalog({
        type: "ModalogConfirmedGiftsSpoiler",
        props: { list: giftlist.value },
    });
}

// Deep links straight to list.confirmed still get the spoiler warning.
onMounted(() => {
    if (isConfirmedView.value && !wishlistStore.isSpoiled) {
        openSpoilerModalog();
    } else if (!isConfirmedView.value) {
        wishlistStore.isSpoiled = false;
    }
});
</script>

<template>
    <div>
        <Head :title="`${giftlist.name} | ${ownerName}`">
            <meta name="description" :content="pageDescription" />
            <meta
                property="og:title"
                :content="`${ownerName}'s “${giftlist.name}” Wish List`"
            />
            <meta
                property="og:description"
                :content="`Give ${ownerName} the perfect gift from their “${giftlist.name}” wish list`"
            />
            <meta property="og:url" :content="openGraph?.url" />
            <meta property="og:image" :content="openGraph?.share_image" />
            <link rel="canonical" :href="openGraph?.url" />
            <meta v-if="noIndex" name="robots" content="noindex, follow" />
        </Head>

        <!-- Page card per Figma 224-2291: on desktop, when there are more
             lists, the white card fades into the page cream over its last
             420px (anchored to the bottom, so the fade reads the same however
             many gifts the list has) and ends after More Lists; without them
             the gift panel ends 44px above the card bottom like the owner
             page (224-2044). The promo sits below on the page cream (BH-53,
             BH-59). -->
        <div
            :class="
                moreLists.length
                    ? 'lg:bg-white lg:bg-[linear-gradient(to_bottom,#fff,#ece7e3)] lg:bg-[length:100%_420px] lg:bg-bottom lg:bg-no-repeat'
                    : 'lg:pb-[44px]'
            "
        >
            <!-- The gift panel ends above More Lists or the card bottom, so
                 its bottom corners show (BH-53), like the item detail panel. -->
            <WishListDetailHero
                :owner="owner"
                :is-owner="false"
                :theme="owner.profile_theme"
                :list="giftlist"
                round-bottom
            >
                <template #mobile-band>
                    <WishListMasthead
                        variant="mobile-banner"
                        :list="giftlist"
                        :is-owner="false"
                        :expert="isGiftHeroExpertList"
                    />
                </template>

                <!-- Mobile: the white hero card wraps the masthead and the Follow
                 button (GH-1079, like the My Lists hero); cream resumes at the
                 gifts heading below. -->
                <div
                    class="max-lg:rounded-b-[10px] max-lg:bg-white lg:contents"
                >
                    <WishListMasthead
                        :list="giftlist"
                        :is-owner="false"
                        :expert="isGiftHeroExpertList"
                    />
                    <div class="px-4 pb-6 pt-2 lg:hidden">
                        <FollowButton :owner="owner" class="w-full" />
                    </div>
                </div>

                <div class="px-4 pb-10 pt-2 lg:px-[44px] lg:pb-[44px]">
                    <!-- Confirmed-gifts (spoiler) view -->
                    <template v-if="isConfirmedView">
                        <template v-if="wishlistStore.isSpoiled">
                            <header
                                class="grid items-center gap-4 pt-4 max-lg:gap-5 max-lg:pt-10 lg:grid-cols-[1fr_auto_1fr]"
                            >
                                <ConfirmedGiftsToggle
                                    variant="hide"
                                    :list="giftlist"
                                    class="max-lg:hidden lg:justify-self-start"
                                />
                                <h2
                                    class="text-center font-brand text-[25px] font-bold text-[#102a3d]"
                                >
                                    {{ confirmedHeading }}
                                </h2>
                                <span class="max-lg:hidden"></span>
                            </header>

                            <div
                                v-if="items.length"
                                class="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                            >
                                <GiftCard
                                    v-for="item in items"
                                    :key="item.uuid"
                                    :item="item"
                                    :list="giftlist"
                                    :is-owner="false"
                                    :confirmed="true"
                                />
                            </div>
                            <p
                                v-else
                                class="mt-10 text-center font-brand text-[16px] text-[#565a77]"
                            >
                                No gifts have been confirmed for this wish list
                                yet.
                            </p>

                            <ListsPagination
                                v-if="meta?.last_page > 1"
                                :page="meta.current_page"
                                :pages="meta.last_page"
                                class="mt-10"
                                @update:page="goToPage"
                            />

                            <!-- Mobile: hide button follows the grid -->
                            <ConfirmedGiftsToggle
                                variant="hide"
                                :list="giftlist"
                                class="mt-8 w-full lg:hidden"
                            />
                        </template>
                    </template>

                    <!-- Standard list view -->
                    <template v-else>
                        <header
                            class="grid items-center gap-4 pt-4 max-lg:gap-5 max-lg:pt-10 lg:grid-cols-[1fr_auto_1fr]"
                        >
                            <ConfirmedGiftsToggle
                                v-if="showConfirmedButton"
                                :list="giftlist"
                                class="max-lg:hidden lg:justify-self-start"
                            />
                            <span v-else class="max-lg:hidden"></span>
                            <h2
                                class="text-center font-brand text-[25px] font-bold text-[#102a3d]"
                            >
                                {{ giftsHeading }}
                            </h2>
                            <SortDropdown
                                v-if="showSort"
                                v-model="sort"
                                :options="SORT_OPTIONS"
                                class="lg:justify-self-end"
                            />
                        </header>

                        <div
                            class="mt-6 grid gap-6 max-lg:gap-[22px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                        >
                            <GiftCard
                                v-for="item in items"
                                :key="item.uuid"
                                :item="item"
                                :list="giftlist"
                                :is-owner="false"
                            />
                        </div>
                        <p
                            v-if="!items.length"
                            class="mt-10 text-center font-brand text-[16px] text-[#565a77]"
                        >
                            {{ ownerShortName }} hasn&apos;t added any gifts to
                            this list yet. Check back!
                        </p>

                        <ListsPagination
                            v-if="meta?.last_page > 1"
                            :page="meta.current_page"
                            :pages="meta.last_page"
                            class="mt-10"
                            @update:page="goToPage"
                        />

                        <!-- Mobile: confirmed-gifts button follows the grid -->
                        <ConfirmedGiftsToggle
                            v-if="showConfirmedButton"
                            :list="giftlist"
                            class="mt-8 w-full lg:hidden"
                        />
                    </template>
                </div>
            </WishListDetailHero>

            <!-- More lists, outside the themed panel, in the main column (right
             of the 282px rail) on desktop; the card's cream fade behind it
             makes the white list cards read as cards. -->
            <div
                v-if="moreLists.length"
                class="px-4 max-lg:bg-brand-cream lg:pb-[44px] lg:pl-[282px] lg:pr-[35px]"
            >
                <section class="pt-10 lg:pt-8">
                    <h2
                        class="text-center font-brand text-[25px] font-bold text-[#102a3d]"
                    >
                        More Lists from {{ moreListsOwnerName }}
                    </h2>
                    <div class="mt-6 grid gap-6 lg:grid-cols-2 lg:gap-[34px]">
                        <WishListCard
                            v-for="list in moreLists"
                            :key="list.uuid"
                            :list="list"
                            :is-owner="false"
                        />
                    </div>
                </section>
            </div>
        </div>

        <!-- The promo sits below the card on the page cream (Figma puts it
             outside the card), aligned to the same column. -->
        <div
            class="bg-brand-cream px-4 pb-10 pt-10 lg:pb-[44px] lg:pl-[282px] lg:pr-[35px]"
            :class="{ 'lg:pt-0': moreLists.length }"
        >
            <PromoBanner
                headline="Need Some
Giftspiration?"
                body="Browse hand-picked gift ideas from our experts and find something they’ll actually love — for any occasion, any budget."
                cta-label="View Expert Lists"
                :cta-href="route('explore')"
                image="images/profile/cta-collage.png"
                mobile-image="images/profile/cta-collage-mobile.png"
                variant="red"
            />
        </div>
    </div>
</template>
